import type React from 'react';
import { useState, useCallback, useRef } from 'react';
import { useFileValidation } from './useFileValidation';
import type {
  FileWithPreview,
  FileValidationConfig,
  FileProcessingResult,
  FileUploadMode,
} from './FileUpload.types';

export interface UseFileUploadProps {
  files: FileWithPreview[];
  onFilesChange: (files: FileWithPreview[]) => void;
  config: FileValidationConfig;
  mode: FileUploadMode;
  disabled?: boolean;
}

export const useFileUpload = ({
  files,
  onFilesChange,
  config,
  mode,
  disabled = false,
}: UseFileUploadProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { validateFiles } = useFileValidation(config);

  const processFiles = useCallback(
    (newFiles: FileList | File[]): FileProcessingResult => {
      if (disabled) {
        return { validFiles: [], errors: [] };
      }

      const fileArray = Array.from(newFiles);

      if (mode === 'single') {
        // Single mode: validate only the first file, replace existing
        const firstFile = fileArray[0];
        if (!firstFile) {
          return { validFiles: [], errors: [] };
        }

        const { validFiles, errors } = validateFiles([firstFile], 0);
        const fileWithPreview: FileWithPreview[] = validFiles.map(
          (file) => file as FileWithPreview
        );

        setValidationErrors(errors);
        if (fileWithPreview.length > 0) {
          onFilesChange(fileWithPreview);
        }

        return { validFiles: fileWithPreview, errors };
      }

      // Multi mode: append to existing files
      const { validFiles, errors } = validateFiles(fileArray, files.length);
      const fileWithPreview: FileWithPreview[] = validFiles.map(
        (file) => file as FileWithPreview
      );

      setValidationErrors(errors);
      if (fileWithPreview.length > 0) {
        onFilesChange([...files, ...fileWithPreview]);
      }

      return { validFiles: fileWithPreview, errors };
    },
    [files, onFilesChange, disabled, validateFiles, mode]
  );

  const handleDragEnter = useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (!disabled) {
        setDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((ev: React.DragEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDragOver = useCallback((ev: React.DragEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      setDragOver(false);

      if (disabled) return;

      const { files: droppedFiles } = ev.dataTransfer;
      if (droppedFiles) {
        processFiles(droppedFiles);
      }
    },
    [processFiles, disabled]
  );

  const handleFileInputChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const { files: selectedFiles } = ev.target;
      if (selectedFiles) {
        processFiles(selectedFiles);
      }
      // Clear the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      if (disabled) return;
      const updatedFiles = files.filter((_, idx) => idx !== index);
      setValidationErrors([]);
      onFilesChange(updatedFiles);
    },
    [files, onFilesChange, disabled]
  );

  const handleZoneClick = useCallback(
    (ev?: React.MouseEvent) => {
      ev?.stopPropagation();
      if (!disabled && fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    [disabled]
  );

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (!disabled && (ev.key === 'Enter' || ev.key === ' ')) {
        ev.preventDefault();
        handleZoneClick();
      }
    },
    [disabled, handleZoneClick]
  );

  return {
    dragOver,
    fileInputRef,
    validationErrors,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    handleRemoveFile,
    handleZoneClick,
    handleKeyDown,
    processFiles,
  };
};
